// Copyright (c) Ping. All rights reserved.

import type { TaskFilter, TaskItem, TaskPriority, TaskSort } from './types';

const PRIORITY_ORDER: Record<TaskPriority, number> = {
  urgent: 4,
  high: 3,
  medium: 2,
  low: 1,
};

/**
 * Pure predicate. `cancelled` is excluded from every filter except when
 * the host explicitly wants it surfaced (out of scope for v1).
 */
export function filterTask(task: TaskItem, filter: TaskFilter): boolean {
  if (task.state === 'cancelled') {
    return false;
  }
  switch (filter) {
    case 'all':
      return true;
    case 'pending':
      return task.state === 'pending' || task.state === 'inProgress';
    case 'completed':
      return task.state === 'completed';
  }
}

/**
 * Pure case-insensitive substring search across `title`. Returns the
 * input list when `query` is empty.
 */
export function searchTasks(tasks: readonly TaskItem[], query: string): readonly TaskItem[] {
  const trimmed = query.trim();
  if (trimmed.length === 0) {
    return tasks;
  }
  const lower = trimmed.toLowerCase();
  return tasks.filter((task) => task.title.toLowerCase().includes(lower));
}

/**
 * Pure comparator-based sort. Returns a new array; never mutates the
 * input. `dueDate` orders nulls after concrete values; `mostRecent`
 * orders newest first.
 */
export function sortTasks(tasks: readonly TaskItem[], sort: TaskSort): readonly TaskItem[] {
  const copy = tasks.slice();
  switch (sort) {
    case 'mostRecent':
      copy.sort((a, b) => b.updatedAt - a.updatedAt);
      return copy;
    case 'priority':
      copy.sort((a, b) => PRIORITY_ORDER[b.priority] - PRIORITY_ORDER[a.priority]);
      return copy;
    case 'dueDate':
      copy.sort((a, b) => {
        if (a.dueAt === null && b.dueAt === null) {
          return 0;
        }
        if (a.dueAt === null) {
          return 1;
        }
        if (b.dueAt === null) {
          return -1;
        }
        return a.dueAt - b.dueAt;
      });
      return copy;
  }
}
